/**
 * Input bugs (epic #581) — keep Input member picks pointing at a renamed
 * struct.
 *
 * An Input step stores a plain pick as the resource id, but a struct member
 * pick as a NAME path: `student.roll`, `student.addr.city`, `students[i].gpa`
 * (InputResourcesSelector in ResourceForms.jsx; codegen looks the root up by
 * name). Renaming `student` → `stud` left `student.roll` behind, pointing at
 * nothing. These helpers rewrite the root of every such path, at any depth.
 *
 * Only an exact root matches: `student.` and `student[` are rewritten,
 * `students.x` is not.
 */

/** `student.roll` → `stud.roll` when renaming student → stud; else unchanged. */
export function renameRefRoot(ref, oldName, newName) {
  if (typeof ref !== 'string' || !oldName || !newName || oldName === newName) return ref;
  if (ref === oldName) return newName;
  if (ref.startsWith(`${oldName}.`) || ref.startsWith(`${oldName}[`)) {
    return newName + ref.slice(oldName.length);
  }
  return ref;
}

const CHILD_ARRAY_KEYS = ['loopBody', 'afterLast', 'children', 'trueBranch', 'falseBranch', 'body'];
const CHILD_BLOCK_KEYS = ['outcomes', 'branches', 'cases'];

/**
 * Rewrite Input `selectedResources` paths rooted at `oldName`, at any depth.
 * Returns the SAME array when nothing changed, so callers can skip a write.
 */
export function renameInputPathsInNodes(nodes, oldName, newName) {
  if (!Array.isArray(nodes) || !oldName || !newName || oldName === newName) return nodes;
  let changed = false;
  const out = nodes.map((node) => {
    if (!node || typeof node !== 'object') return node;
    let next = node;
    const touch = () => { if (next === node) next = { ...node }; };

    if (node.type === 'input' && Array.isArray(node.selectedResources)) {
      const sel = node.selectedResources.map((r) => renameRefRoot(r, oldName, newName));
      if (sel.some((r, i) => r !== node.selectedResources[i])) {
        touch();
        next.selectedResources = sel;
      }
    }
    for (const key of CHILD_ARRAY_KEYS) {
      if (!Array.isArray(node[key])) continue;
      const kids = renameInputPathsInNodes(node[key], oldName, newName);
      if (kids !== node[key]) { touch(); next[key] = kids; }
    }
    for (const key of CHILD_BLOCK_KEYS) {
      if (!Array.isArray(node[key])) continue;
      let blockChanged = false;
      const blocks = node[key].map((b) => {
        if (!b || !Array.isArray(b.nodes)) return b;
        const kids = renameInputPathsInNodes(b.nodes, oldName, newName);
        if (kids === b.nodes) return b;
        blockChanged = true;
        return { ...b, nodes: kids };
      });
      if (blockChanged) { touch(); next[key] = blocks; }
    }
    if (next !== node) changed = true;
    return next;
  });
  return changed ? out : nodes;
}
